import type { Marker } from '../../domain/annotation';
import { bloeckeBei, type Block } from '../../domain/videoAnnotation';

/**
 * Was auf der Zeichenebene steht — und was davon zum ausgewählten Band gehört.
 *
 * Eigene, React-freie Datei: hier wird entschieden, was der Prüfer überhaupt anfassen kann,
 * und das soll ohne Bildschirm prüfbar bleiben (dieselbe Linie wie `leiste.ts`).
 */

export interface Sicht {
  /** Marken der übrigen gerade geltenden Bänder — blass gezeichnet, aber anfassbar. */
  fremd: Marker[];
  /** Marken des ausgewählten Bandes. Sie liegen oben. */
  eigen: Marker[];
}

/**
 * ⚠️ **Die Zeit entscheidet, nicht die Auswahl.** Ein gewähltes Band, dessen Zeit gerade nicht
 * gilt, ist **weg** — Befund des Nutzers vom 2026-09-20, nachdem er die umgekehrte Regel am
 * Gerät gesehen hat. Sie zeigte einen Stand, den die Wiedergabe dort nie zeigt, und beim Ziehen
 * des Strichs verlor man das Gefühl dafür, wann eine Marke erscheint und wann sie geht.
 *
 * Herankommen tut man trotzdem an jedes Band: die Auswahl in der Leiste springt an seinen
 * Anfang (`VideoAnnotateSheet.tsx::waehleBand`). Dann gilt es, und dann ist es zu sehen.
 *
 * ⚠️ **Fremde Marken bleiben anfassbar.** Sie nur blass zu zeichnen und tot zu stellen hiesse,
 * dass man ein überdecktes Band nur über die Leiste erreicht — und dort ist es bei vielen
 * Spuren ein schmaler Streifen. Ein Tipp auf eine fremde Marke wählt ihr Band (`bandVon`).
 */
export function sichtbareMarken(
  blocks: Block[],
  positionMs: number,
  auswahl: string | null,
): Sicht {
  const geltend = bloeckeBei(blocks, positionMs);
  const gewaehlt = auswahl === null ? null : (blocks.find((b) => b.id === auswahl) ?? null);

  const giltJetzt = gewaehlt !== null && geltend.some((b) => b.id === gewaehlt.id);

  return {
    fremd: geltend.filter((b) => b.id !== gewaehlt?.id).flatMap((b) => b.markers),
    eigen: giltJetzt ? (gewaehlt?.markers ?? []) : [],
  };
}

/** In welchem Band liegt diese Marke? `null`, wenn sie zu keinem gehört. */
export function bandVon(blocks: Block[], markeId: string): string | null {
  return blocks.find((b) => b.markers.some((m) => m.id === markeId))?.id ?? null;
}
